import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  inject,
  afterNextRender,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Screen, WindowService } from '@modules/shared/services/window.service';

import { SliderBaseInterface, SliderBaseItemInterface } from '../interfaces/slider.interface';
import { SliderNavigationComponent } from '../slider-navigation/slider-navigation.component';
import { SliderUtilReorder } from '../utils/slider.utils';

@Component({
  selector: 'mb-slider-base',
  templateUrl: './slider-base.component.html',
  styleUrls: ['./slider-base.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [NgClass, NgStyle, NgTemplateOutlet, SliderNavigationComponent],
})
export class SliderBaseComponent<T extends { id?: string | number }> implements AfterViewInit, OnDestroy {
  private elRef = inject(ElementRef);
  private windowService = inject(WindowService);
  private cdr = inject(ChangeDetectorRef);

  // required for testing purpose to disable animations
  public disableAnimations = false;

  public insufficientItems = false;

  private destroy$ = new Subject<boolean>();
  private _config!: SliderBaseInterface;
  private _activeConfig!: SliderBaseInterface;
  private cloneCount = 3;
  private isSliding = false;
  private trackLength = 1;
  private _items: T[] = [];
  private initializedConfig = false;
  // Stays false on the server and during the pre-hydration client render so `config` resolves
  // against Screen.XXXS on both sides; afterNextRender flips it once hydration is confirmed.
  private hydrated = false;

  @Input() initialIndex = 0;

  public activeIndex = 0;

  public navItems = [];
  public originalItems: T[] = [];

  public style = {
    minWidth: '100%',
    maxWidth: '100%',
  };

  @ContentChild(TemplateRef, { static: false }) itemTemplate!: TemplateRef<any>;
  @ViewChild('track', { static: false }) track!: ElementRef<any>;
  @ViewChild('navigation', { static: false }) navigation!: ElementRef<any>;

  @Output() indexChanged = new EventEmitter<number>();

  @Input() inGrid = true;
  // containerClass can only be set if inGrid is true
  @Input() containerClass: string | string[] = ['container'];
  @Input() itemsToSlide = 1;

  @Input()
  set config(v: SliderBaseInterface) {
    /**
     * the config should be set as last input when using the base
     */
    this._config = v;
    this._activeConfig = v;
    const config = this.getActiveScreenConfig(this.hydrated ? this.windowService.screen : Screen.XXXS);
    if (config) {
      this.setConfig(config);
    } else {
      this.setConfig(v);
    }
  }
  get config() {
    return this._config;
  }

  @Input()
  set items(_list: T[]) {
    this.originalItems = _list;
    this.trackLength = _list.length;
    this.navItems.length = Math.ceil(this.trackLength / this.itemsToSlide);
    this._items = [];
    for (let i = 0; i < this.cloneCount; i++) {
      _list.forEach((data: T, _index: number) => {
        this._items.push(data);
      });
    }
  }
  get items() {
    return this._items;
  }

  private _itemsToShow = 1;
  @Input()
  set itemsToShow(v: number) {
    this._itemsToShow = v;
    this.style = {
      minWidth: 100 / v + '%',
      maxWidth: 100 / v + '%',
    };
  }
  get itemsToShow() {
    return this._itemsToShow;
  }

  public next() {
    if (this.isSliding && !this.disableAnimations) {
      return;
    }
    this.setTransformPos(true);
  }

  public prev() {
    if (this.isSliding && !this.disableAnimations) {
      return;
    }
    this.setTransformPos(false);
  }

  public slideTo(index: number) {
    if (this.isSliding && !this.disableAnimations) {
      return;
    }
    const toRight = this.activeIndex < index;
    this.setTransformPos(toRight, false, index);
    this.activeIndex = index;
    this.updateIndex();
  }

  private setTransformPos(toRight: boolean | null, updateIndex = true, nextSlide = 0, animate = true) {
    const activeIndex = this.activeIndex;
    if (updateIndex) {
      if (updateIndex && toRight) {
        this.activeIndex++;
      } else {
        this.activeIndex--;
      }
      nextSlide = this.activeIndex;
      this.updateIndex();
    }
    const itemsToSlide = this.getSlideCount(activeIndex, nextSlide);

    const track = this.track.nativeElement;
    track.classList.add('is-animating');
    this.isSliding = true;
    const animateCb = () => {
      track.classList.remove('is-animating');
      if (toRight !== null) {
        for (let i = 0; i < itemsToSlide; i++) {
          this.reorder(toRight);
        }
      }
      this.elRef.nativeElement.style.setProperty(
        '--carousel-slider-left-pos',
        `${(100 / this.itemsToShow) * this.trackLength * -1}%`
      );
      this.isSliding = false;
    };
    if (animate && !this.disableAnimations) {
      setTimeout(() => {
        animateCb();
      }, 400);
    } else {
      animateCb();
    }
    const leftPos = (100 / this.itemsToShow) * ((this.trackLength + (toRight ? 1 : -1) * itemsToSlide) * -1);
    this.elRef.nativeElement.style.setProperty('--carousel-slider-left-pos', `${leftPos}%`);
  }

  private reorder(toRight: boolean) {
    SliderUtilReorder(toRight, this.track.nativeElement);
  }

  private getSlideCount(activeIndex: number, slideTo: number): number {
    const toRight = activeIndex < slideTo;
    let itemsToSlide = this.itemsToSlide;
    if (toRight) {
      const diff = slideTo - activeIndex;
      itemsToSlide = this.itemsToSlide * diff;
      if (slideTo === this.navItems.length) {
        itemsToSlide = (diff - 1) * this.itemsToSlide + (this.trackLength % this.itemsToSlide) || this.itemsToSlide;
      }
    } else {
      const diff = activeIndex - slideTo;
      itemsToSlide = this.itemsToSlide * diff;
      if (slideTo < 0) {
        itemsToSlide = (diff - 1) * this.itemsToSlide + (this.trackLength % this.itemsToSlide) || this.itemsToSlide;
      }
    }

    return itemsToSlide;
  }

  public dragStart(e: MouseEvent | TouchEvent) {
    let dragged = false;
    let startPos: any;
    if (e.type === 'mousedown') {
      startPos = (e as MouseEvent).screenX;
    } else if ((e as TouchEvent).touches?.length) {
      startPos = (e as TouchEvent).touches[0].screenX;
    } else {
      return;
    }
    const track = this.track.nativeElement;
    const trackWidthPercent = track.clientWidth / 100;
    const threshHold = trackWidthPercent * (100 / this.itemsToShow) * this.itemsToSlide;

    const currentLeft = (100 / this.itemsToShow) * this.trackLength * -1;
    let newValue = 0;
    document.body.style.userSelect = 'none';

    track.style.transform = `translateX(${Math.round(currentLeft + newValue / trackWidthPercent)}%)`;
    const mousemove = (e: MouseEvent | TouchEvent) => {
      if (!dragged) {
        dragged = true;
        track.classList.add('is-dragging');
      }
      let screenX: any;
      if (e.type === 'mousemove') {
        screenX = (e as MouseEvent).screenX;
      } else if ((e as TouchEvent).touches?.length) {
        screenX = (e as TouchEvent).touches[0].screenX;
      } else {
        return;
      }
      newValue = screenX - startPos;
      const threshHoldReached = Math.abs(newValue) > threshHold;
      if (threshHoldReached) {
        const isRight = newValue < 0;
        startPos = isRight ? startPos - threshHold : startPos + threshHold;
        if (isRight) {
          this.activeIndex++;
        } else {
          this.activeIndex--;
        }
        this.updateIndex();
        this.reorder(isRight);
      }
      newValue = screenX - startPos;
      track.style.transform = `translateX(${Math.round(currentLeft + newValue / trackWidthPercent)}%)`;
    };

    const dragEnd = () => {
      track.style.transform = null;
      track.classList.remove('is-dragging');

      this.elRef.nativeElement.style.setProperty(
        '--carousel-slider-left-pos',
        `${(100 / this.itemsToShow) * this.trackLength * -1}%`
      );
      if (Math.abs(newValue * 4) > threshHold) {
        this.setTransformPos(newValue < 0);
      } else {
        track.classList.add('is-animating');
        setTimeout(() => {
          track.classList.remove('is-animating');
        }, 300);
      }

      document.removeEventListener('mousemove', mousemove);
      document.removeEventListener('touchmove', mousemove);
      document.removeEventListener('mouseup', dragEnd);
      document.removeEventListener('touchend', dragEnd);
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', mousemove);
    document.addEventListener('touchmove', mousemove);
    document.addEventListener('mouseup', dragEnd);
    document.addEventListener('touchend', dragEnd);
  }

  private updateIndex() {
    if (this.activeIndex >= this.navItems.length) {
      this.activeIndex = this.activeIndex - this.navItems.length;
    } else if (this.activeIndex < 0) {
      this.activeIndex = this.navItems.length - 1;
    }
    this.cdr.markForCheck();
    this.indexChanged.emit(this.activeIndex);
  }

  private setConfig(config: SliderBaseItemInterface | SliderBaseInterface) {
    const { inGrid, itemsToSlide, itemsToShow } = config;
    this._activeConfig = config;
    const insufficientItems = itemsToShow >= this.originalItems.length;
    this.inGrid = !!inGrid;
    this.itemsToSlide = itemsToSlide;
    this.itemsToShow = itemsToShow;
    this.navItems.length = Math.ceil(this.trackLength / this.itemsToSlide);
    if (this.insufficientItems !== insufficientItems) {
      this.insufficientItems = insufficientItems;
      this.cdr.detectChanges();
    }
    if (!this.track) {
      return;
    }
    this.track.nativeElement.childNodes.forEach((el: HTMLElement, index: number) => {
      if (!el?.style) {
        return;
      }
      el.style.order = `${index + 1}`;
    });
    this.activeIndex = 0;
    this.setTransformPos(true, false, this.initialIndex);
    this.activeIndex = this.initialIndex;

    this.cdr.markForCheck();
  }

  constructor() {
    // Never runs during SSR, so this is where the real screen-driven config is safely applied.
    afterNextRender(() => {
      this.hydrated = true;
      this.initializedConfig = true;
      this.setConfig(this.getActiveScreenConfig(this.windowService.screen));
    });
  }

  ngAfterViewInit() {
    this.track?.nativeElement.childNodes.forEach((el: HTMLElement, index: number) => {
      if (!el?.style) {
        return;
      }
      el.style.order = `${index + 1}`;
    });

    this.windowService.screen$.pipe(takeUntil(this.destroy$)).subscribe((screen) => {
      if (!this.hydrated) {
        return;
      }
      const config = this.getActiveScreenConfig(screen);
      if (
        this._activeConfig.inGrid !== config.inGrid ||
        this._activeConfig.itemsToShow !== config.itemsToShow ||
        this._activeConfig.itemsToSlide !== config.itemsToSlide ||
        !this.initializedConfig
      ) {
        this.initializedConfig = true;
        this.setConfig(config);
      }
    });
  }

  /**
   * only used internally
   * only public for testing purpose
   */
  public getActiveScreenConfig(screen: Screen): SliderBaseItemInterface {
    let config: SliderBaseItemInterface | null = null;
    if (this.config.breakpoints) {
      if (this.config.breakpoints[screen]) {
        config = this.config.breakpoints[screen] as SliderBaseItemInterface;
      } else {
        const keys = Object.keys(this.config.breakpoints).map((key) => parseInt(key, 10)) as unknown as number[];
        const possibleConfigs = keys.sort((a, b) => a - b).filter((w) => w < (screen as number)) as Screen[];
        if (possibleConfigs?.length) {
          config = this.config.breakpoints[possibleConfigs[possibleConfigs.length - 1]] as SliderBaseItemInterface;
        }
      }
    }
    if (!config) {
      config = this.config;
    }
    return config as SliderBaseItemInterface;
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
